import React, { useMemo } from 'react';

function computeProductImpact(entries) {
  if (entries.length < 5) return [];

  const products = {};

  entries.forEach(e => {
    const all = [...(e.routines?.AM || []), ...(e.routines?.PM || [])];
    all.forEach(p => {
      if (!products[p]) products[p] = { with: [], without: [] };
    });
  });

  entries.forEach(e => {
    const used = new Set([...(e.routines?.AM || []), ...(e.routines?.PM || [])]);
    Object.keys(products).forEach(p => {
      (used.has(p) ? products[p].with : products[p].without).push(e.glowScore);
    });
  });

  return Object.entries(products)
    .map(([name, data]) => {
      if (data.with.length < 2 || data.without.length < 1) return null;
      const avgWith = data.with.reduce((s, v) => s + v, 0) / data.with.length;
      const avgWithout = data.without.reduce((s, v) => s + v, 0) / data.without.length;
      return { name, avgWith, avgWithout, impact: avgWith - avgWithout, uses: data.with.length };
    })
    .filter(Boolean)
    .sort((a, b) => Math.abs(b.impact) - Math.abs(a.impact));
}

function ImpactRow({ item, positive }) {
  const barW = Math.min(72, Math.abs(item.impact) * 18);
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <p className="font-sans text-sm text-stone-700 font-medium truncate">{item.name}</p>
        <p className="font-sans text-xs text-stone-400">{item.uses}× used · avg {item.avgWith.toFixed(1)}/10</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="h-1.5 rounded-full"
          style={{ width: barW, background: positive ? '#10b981' : '#f43f5e' }} />
        <span className="font-sans text-xs font-bold w-10 text-right"
          style={{ color: positive ? '#059669' : '#e11d48' }}>
          {positive ? '+' : ''}{item.impact.toFixed(1)}
        </span>
      </div>
    </div>
  );
}

export default function ProductInsightsCard({ entries }) {
  const insights = useMemo(() => computeProductImpact(entries), [entries]);

  if (entries.length < 5 || insights.length === 0) {
    return (
      <div className="bg-white rounded-3xl border border-stone-100 shadow-sm p-6 text-center">
        <p className="text-2xl mb-2">🧪</p>
        <p className="font-sans text-sm font-semibold text-stone-700 mb-1">Not enough data yet</p>
        <p className="font-sans text-xs text-stone-400 leading-relaxed">
          Log at least 5 entries with varied products to see which ones boost your glow.
        </p>
      </div>
    );
  }

  const boosters = insights.filter(p => p.impact > 0.2).slice(0, 5);
  const drainers = insights.filter(p => p.impact < -0.2).slice(0, 3);

  return (
    <div className="bg-white rounded-3xl border border-stone-100 shadow-sm overflow-hidden">
      <div className="px-5 pt-5 pb-3 border-b border-stone-50">
        <p className="font-sans text-sm font-semibold text-stone-800 mb-0.5">Product Effectiveness</p>
        <p className="font-sans text-xs text-stone-400">Which products correlate with your best skin days</p>
      </div>

      {boosters.length > 0 && (
        <div className="px-5 py-4">
          <p className="font-sans text-xs font-semibold text-emerald-600 uppercase tracking-widest mb-3">✓ Glow Boosters</p>
          <div className="space-y-3">
            {boosters.map(p => <ImpactRow key={p.name} item={p} positive />)}
          </div>
        </div>
      )}

      {drainers.length > 0 && (
        <div className="px-5 py-4 border-t border-stone-50">
          <p className="font-sans text-xs font-semibold text-rose-500 uppercase tracking-widest mb-3">↓ Watch These</p>
          <div className="space-y-3">
            {drainers.map(p => <ImpactRow key={p.name} item={p} positive={false} />)}
          </div>
        </div>
      )}

      {boosters.length === 0 && drainers.length === 0 && (
        <div className="px-5 py-6 text-center">
          <p className="font-sans text-sm text-stone-400">No strong correlations yet — keep logging!</p>
        </div>
      )}

      <div className="px-5 py-2.5 bg-stone-50 border-t border-stone-100">
        <p className="font-sans text-xs text-stone-400">
          Impact = avg glow with − without · {entries.length} entries · correlation only, not causation
        </p>
      </div>
    </div>
  );
}
